import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Store, Coins, Clock, Wallet, ArrowUpRight, TrendingUp, Copy, Check,
  BadgeCheck, Banknote, ArrowDownToLine,
} from 'lucide-react';

const inr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdt = (v) => `${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`;

const RANGES = [
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '3m', label: '3M' },
  { key: '12m', label: '12M' },
];

export default function ResellerDashboardPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [range, setRange] = useState('30d');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/reseller/analytics?range=${range}`);
      setD(r.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    }
  }, [api, range]);

  useEffect(() => { load(); }, [load]);

  const signupLink = d ? `${window.location.origin}/register?reseller=${d.signup_code}` : '';
  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(signupLink);
      } else {
        const ta = document.createElement('textarea');
        ta.value = signupLink; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true); toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy — select the link to copy manually'); }
  };

  if (!d) {
    return (
      <div className="space-y-6" data-testid="reseller-dashboard">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const k = d.kpis;
  const w = d.wallet;
  const kpis = [
    { icon: Store, label: 'Total Merchants', value: k.total_merchants, sub: `${k.active_merchants} active`, tone: 'text-primary', bg: 'bg-primary/10' },
    { icon: BadgeCheck, label: 'Active Merchants', value: k.active_merchants, sub: 'KYC-ready & live', tone: 'text-blue-600', bg: 'bg-blue-500/10' },
    { icon: Coins, label: 'Total Earnings', value: inr(k.total_earned_inr), sub: usdt(k.total_earned_usdt), tone: 'text-green-600', bg: 'bg-green-500/10' },
    { icon: Clock, label: 'Pending Earnings', value: inr(k.pending_inr), sub: 'awaiting confirmation', tone: 'text-amber-600', bg: 'bg-amber-500/10' },
    { icon: Wallet, label: 'Available USDT', value: usdt(w.available_usdt), sub: inr(w.available_inr), tone: 'text-emerald-600', bg: 'bg-emerald-500/10' },
    { icon: ArrowDownToLine, label: 'Total Withdrawn', value: usdt(w.total_withdrawn_usdt), sub: inr(w.total_withdrawn_inr), tone: 'text-slate-600', bg: 'bg-slate-500/10' },
  ];

  return (
    <div className="space-y-6" data-testid="reseller-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Welcome, {d.reseller?.business_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-sm text-primary font-semibold">{d.reseller?.reseller_code}</span>
            <Badge variant="outline" className="rounded-sm text-[10px] bg-green-500/15 text-green-600 border-green-500/30">
              {(d.reseller?.status || '').toUpperCase()}
            </Badge>
          </div>
        </div>
        <Button onClick={() => navigate('/reseller/withdrawals')} className="rounded-sm" data-testid="go-withdrawals-btn">
          <Banknote className="w-4 h-4 mr-2" />Withdraw Earnings
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((c) => (
          <Card key={c.label} data-testid={`kpi-${c.label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{c.label}</p>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${c.bg}`}><c.icon className={`h-4 w-4 ${c.tone}`} /></div>
              </div>
              <p className="text-2xl font-bold font-heading mt-2 leading-none">{c.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Earnings Wallet + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Wallet card */}
        <Card className="lg:col-span-1 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-primary/20" data-testid="earnings-wallet-card">
          <CardContent className="p-6 flex flex-col h-full">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Wallet className="h-4 w-4" />Reseller Earnings Wallet</p>
            <div className="mt-4">
              <p className="text-3xl font-bold font-heading" data-testid="wallet-available-usdt">{usdt(w.available_usdt)}</p>
              <p className="text-muted-foreground mt-1" data-testid="wallet-available-inr">≈ {inr(w.available_inr)}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-border/50 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Current Rate</span>
                <span className="font-mono font-semibold" data-testid="wallet-usdt-rate">{inr(w.usdt_rate)} / USDT</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Live rate configured by AwadhPay</p>
            </div>
            <Button onClick={() => navigate('/reseller/withdrawals')} className="w-full mt-5 rounded-sm" data-testid="wallet-withdraw-btn">
              <ArrowUpRight className="w-4 h-4 mr-2" />Withdraw Earnings
            </Button>
          </CardContent>
        </Card>

        {/* Earnings chart */}
        <Card className="lg:col-span-2" data-testid="earnings-chart-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-heading font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" />Earnings Overview</p>
                <p className="text-xs text-muted-foreground">Confirmed commission over time</p>
              </div>
              <div className="flex gap-1 bg-muted rounded-md p-1">
                {RANGES.map((r) => (
                  <button key={r.key} onClick={() => setRange(r.key)}
                    className={`px-3 py-1 text-xs rounded font-medium transition-colors ${range === r.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    data-testid={`range-${r.key}`}>{r.label}</button>
                ))}
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.earnings_series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="earn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000) + 'k' : v}`} width={48} />
                  <Tooltip formatter={(v) => [inr(v), 'Commission']} labelClassName="text-xs" contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area type="monotone" dataKey="inr" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#earn)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top merchants + signup link */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="top-merchants-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-heading font-semibold">Top Performing Merchants</p>
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/reseller/merchants')}>View all</Button>
            </div>
            {d.top_merchants.length ? (
              <div className="space-y-2">
                {d.top_merchants.map((m, i) => (
                  <div key={m.merchant_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/40" data-testid={`top-merchant-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="h-7 w-7 rounded-md bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{m.business_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{m.mid}</p>
                      </div>
                    </div>
                    <span className="font-mono font-semibold text-green-600">{inr(m.commission_inr)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground py-6 text-center">No commission earned yet.</p>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1" data-testid="signup-link-card">
          <CardContent className="p-5">
            <p className="font-heading font-semibold mb-1">Onboarding Link</p>
            <p className="text-xs text-muted-foreground mb-3">New merchants are tagged to you automatically.</p>
            <div className="font-mono text-xs bg-muted/50 rounded px-3 py-2 break-all" data-testid="reseller-signup-link">{signupLink}</div>
            <Button onClick={copyLink} variant="outline" className="w-full mt-3 rounded-sm" data-testid="copy-signup-link-btn">
              {copied ? <><Check className="w-4 h-4 mr-2" />Copied</> : <><Copy className="w-4 h-4 mr-2" />Copy Link</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
